import React, {
  forwardRef,
  Fragment,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { Portal } from '@gorhom/portal';
import { styled } from 'dripsy';

import { IPopoverManagerProps } from '@hooks';
import { Touchable, TouchableTypeEnum } from '../../common/Touchable';
import {
  PopoverContent,
  PopoverPositionEnum,
} from './PopoverContent';

const TouchableOverlay = styled(Touchable)({
  ...StyleSheet.absoluteFillObject,
  ...Platform.select({ default: { zIndex: 1 } }),
});

export interface IPopoverProps {
  position?: PopoverPositionEnum;
  children: ReactNode[];
  onVisibleChange?(value: boolean): void;
  offset?: number;
}

/**
 * Popover component should have only two children
 * Target content and Popover content
 *
 * @param {IPopoverProps} props
 * @param {IPopoverManagerProps} ref
 * @returns {JSX.Element}
 */
export const Popover = forwardRef<
  IPopoverManagerProps,
  IPopoverProps
>(({ children, onVisibleChange, ...props }, ref) => {
  const targetRef = useRef<View>(null);
  const [visible, setVisible] = useState<boolean>(false);

  useEffect(() => {
    onVisibleChange?.(visible);
  }, [visible]);

  useImperativeHandle(ref, () => ({
    openPopover,
    closePopover,
    togglePopover,
  }));

  function openPopover() {
    setVisible(true);
  }

  function closePopover() {
    setVisible(false);
  }

  function togglePopover() {
    setVisible((prev) => !prev);
  }

  return (
    <Fragment>
      <View ref={targetRef}>{children[0]}</View>

      {visible && (
        <Portal>
          <TouchableOverlay
            onPress={closePopover}
            type={TouchableTypeEnum.WithoutFeedback}
          />
          <PopoverContent
            {...props}
            onClose={closePopover}
            targetRef={targetRef}
          >
            {children[1]}
          </PopoverContent>
        </Portal>
      )}
    </Fragment>
  );
});
